import { Injectable, inject } from '@angular/core';
import { Observable, map } from 'rxjs';
import { ApiService } from '../../Shared/Services/api.service';
import { endpoints } from '../../../../environments/endpoints';
import { PLAN_SCAN_PAGE_SIZE } from '../../../Consts/backoffice.const';
import { LaravelDefaultResponse } from '../../../Interfaces/api/shared/response.interface';
import { MarketListResponse } from '../../../Interfaces/api/backoffice/market.interface';
import {
  PlanListResponse,
  PlanShowResponse,
  PlanSubscriberListResponse,
} from '../../../Interfaces/api/backoffice/plan.interface';
import { TypedResourceRequest } from '../../Shared/Interfaces/api-request.interface';
import { PagedResult } from '../../Shared/Interfaces/paged-result.interface';
import { Market } from '../../../Models/backoffice/market.model';
import { PlanDetail } from '../../../Models/backoffice/plan-detail.model';
import { PlanSubscriber } from '../../../Models/backoffice/plan-subscriber.model';
import { PlanSummary } from '../../../Models/backoffice/plan-summary.model';
import { PlanCreatePayload, PlanFilters, PlanUpdatePayload } from '../Interfaces/plan-filters.interface';

@Injectable({
  providedIn: 'root',
})
export class PlanService {
  private readonly _apiService = inject(ApiService);

  listRequest(): TypedResourceRequest<LaravelDefaultResponse<PlanListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<PlanListResponse>>(
      endpoints.backoffice.plans,
      { params: { page: 0, pageSize: PLAN_SCAN_PAGE_SIZE }, skipLoading: true },
    );
  }

  pagedListRequest(
    filters: PlanFilters,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<PlanListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<PlanListResponse>>(
      endpoints.backoffice.plans,
      { params: this._params(filters, page, pageSize), skipLoading: true },
    );
  }

  showRequest(planId: string): TypedResourceRequest<LaravelDefaultResponse<PlanShowResponse>> {
    return this._apiService.get<LaravelDefaultResponse<PlanShowResponse>>(
      endpoints.backoffice.plan(planId),
      { skipLoading: true },
    );
  }

  marketsRequest(): TypedResourceRequest<LaravelDefaultResponse<MarketListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<MarketListResponse>>(
      endpoints.backoffice.markets,
      { skipLoading: true },
    );
  }

  subscribersRequest(
    planId: string,
    page: number,
    pageSize: number,
  ): TypedResourceRequest<LaravelDefaultResponse<PlanSubscriberListResponse>> {
    return this._apiService.get<LaravelDefaultResponse<PlanSubscriberListResponse>>(
      endpoints.backoffice.planSubscribers(planId),
      { params: { page, pageSize }, skipLoading: true, skipError: true },
    );
  }

  create(payload: PlanCreatePayload): Observable<PlanDetail | null> {
    const request = this._apiService.post<LaravelDefaultResponse<PlanShowResponse>>(
      endpoints.backoffice.plans,
      { body: payload, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<PlanShowResponse>>(request)
      .pipe(map((response) => this.toDetail(response)));
  }

  update(planId: string, payload: PlanUpdatePayload): Observable<PlanDetail | null> {
    const request = this._apiService.put<LaravelDefaultResponse<PlanShowResponse>>(
      endpoints.backoffice.plan(planId),
      { body: payload, skipError: true },
    );

    return this._apiService
      .run<LaravelDefaultResponse<PlanShowResponse>>(request)
      .pipe(map((response) => this.toDetail(response)));
  }

  toPlans(response: LaravelDefaultResponse<PlanListResponse> | undefined): PlanSummary[] {
    return (response?.data?.plans ?? []).map((row) => new PlanSummary(row));
  }

  toPagedResult(
    response: LaravelDefaultResponse<PlanListResponse> | undefined,
  ): PagedResult<PlanSummary> {
    const payload = response?.data;

    return {
      items: (payload?.plans ?? []).map((row) => new PlanSummary(row)),
      total: payload?.total ?? 0,
      page: payload?.page ?? 0,
      pageSize: payload?.pageSize ?? 0,
    };
  }

  toDetail(response: LaravelDefaultResponse<PlanShowResponse> | undefined): PlanDetail | null {
    return response?.data?.plan ? new PlanDetail(response.data.plan) : null;
  }

  toSubscribers(
    response: LaravelDefaultResponse<PlanSubscriberListResponse> | undefined,
  ): PagedResult<PlanSubscriber> {
    const payload = response?.data;

    return {
      items: (payload?.subscribers ?? []).map((row) => new PlanSubscriber(row)),
      total: payload?.total ?? 0,
      page: payload?.page ?? 0,
      pageSize: payload?.pageSize ?? 0,
    };
  }

  toMarkets(response: LaravelDefaultResponse<MarketListResponse> | undefined): Market[] {
    return (response?.data?.markets ?? []).map((row) => new Market(row));
  }

  defaultCurrency(markets: Market[]): string | null {
    const preferred = markets.find((market) => market.is_default && market.is_active);

    return (preferred ?? markets[0])?.currency ?? null;
  }

  private _params(
    filters: PlanFilters,
    page: number,
    pageSize: number,
  ): Record<string, string | number> {
    const params: Record<string, string | number> = { page, pageSize };

    for (const [key, value] of Object.entries(filters)) {
      if (value !== '') {
        params[key] = String(value);
      }
    }

    return params;
  }
}
